
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import LoginForm from '@/components/auth/LoginForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/SupabaseAuthContext';
import { Button } from '@/components/ui/button';

interface LocationState {
  from?: string;
  message?: string;
}

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toast
  } = useToast();
  const [loginInProgress, setLoginInProgress] = useState(false);
  const {
    isAuthenticated,
    loading,
    user,
    role
  } = useAuth();
  const state = location.state as LocationState;
  const returnPath = state?.from || '/dashboard';
  const message = state?.message;

  useEffect(() => {
    if (loading) {
      return;
    }
    if (isAuthenticated && user) {
      console.log('User already authenticated with role:', role);
      if (role === 'masterAdmin' || user.email?.includes('master')) {
        console.log('Master admin detected, redirecting to master dashboard');
        navigate('/master/dashboard', {
          replace: true
        });
        return;
      }
      if (role === 'superadmin') {
        console.log('Super admin detected, redirecting to super admin dashboard');
        navigate('/super-admin/dashboard', {
          replace: true
        });
        return;
      }
      let redirectPath = '/dashboard';
      if (role === 'admin') {
        redirectPath = '/admin/dashboard';
      } else if (role === 'business' || role === 'manager') {
        redirectPath = '/business/dashboard';
      } else if (role === 'user') {
        redirectPath = '/user/dashboard';
      }
      console.log('User already logged in. Redirecting to:', redirectPath);
      navigate(redirectPath, {
        replace: true
      });
    }
  }, [navigate, isAuthenticated, loading, role, user]);

  useEffect(() => {
    if (message) {
      toast({
        title: "Session Expired",
        description: message,
        variant: "default"
      });
    }
  }, [message, toast]);

  const handleLoginStart = () => {
    setLoginInProgress(true);
  };

  const handleLoginSuccess = (userRole: string) => {
    console.log("Login successful with role:", userRole);
    setLoginInProgress(false);
    if (userRole === 'masterAdmin') {
      console.log('Master admin detected, redirecting to master dashboard');
      navigate('/master/dashboard', {
        replace: true
      });
      return;
    }
    if (userRole === 'superadmin') {
      console.log('Super admin detected, redirecting to super admin dashboard');
      navigate('/super-admin/dashboard', {
        replace: true
      });
      return;
    }
    let redirectPath = '/dashboard';
    if (userRole === 'admin') {
      redirectPath = '/admin/dashboard';
    } else if (userRole === 'business' || userRole === 'manager') {
      redirectPath = '/business/dashboard';
    } else if (userRole === 'user') {
      redirectPath = '/user/dashboard';
    }
    console.log("Redirecting to:", redirectPath);
    navigate(redirectPath, {
      replace: true
    });
  };

  const handleLoginError = (error: Error) => {
    setLoginInProgress(false);
    toast({
      title: "Login Failed",
      description: error.message || "Invalid email or password. Please try again.",
      variant: "destructive"
    });
  };

  if (loading || loginInProgress) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">{loginInProgress ? "Logging in..." : "Checking authentication..."}</p>
        </div>
      </div>;
  }

  if (isAuthenticated) {
    return null;
  }

  return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-[40px]">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-primary-blue">Welcome Back</h1>
          <p className="mt-2 text-gray-600">Sign in to your account to continue</p>
        </div>
        
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-2xl font-bold text-center">Sign In</CardTitle>
            <CardDescription className="text-center">Enter your credentials below</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <LoginForm isSaasInstance={false} onLogin={handleLoginSuccess} onError={handleLoginError} onLoginStart={handleLoginStart} />
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <a href="/signup" className="font-medium text-primary hover:text-primary/90 transition-colors">
              Sign up
            </a>
          </p>
          
          <div className="mt-3 text-sm text-gray-500">
            <p className="mb-1">For testing, use one of these accounts:</p>
            <ul className="space-y-1">
              <li>superadmin@example.com / superadmin123</li>
              <li>admin@example.com / admin123</li>
              <li>business@example.com / business123</li>
              <li>master@example.com / master123</li>
              <li>user@example.com / user123</li>
            </ul>
          </div>

          <div className="mt-6">
            <Button variant="link" asChild>
              <Link to="/" className="text-primary-blue hover:underline">
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
};

export default Login;
